
import throttle from 'lodash.throttle';
import moment from 'moment';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Input, Popup, Button, Image, Label, Icon } from 'semantic-ui-react';
import { useQuery, useMutation } from '@apollo/client';
import { useCallback, useState, useEffect } from 'react';

import { PATHS } from '../../../../paths';
import { formatDate } from '../../../../dates';
import { useIsAdmin } from '../../../../hooks/isAdmin';
import { useIsCenterManager } from '../../../../hooks/isCenterManager';
import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { InterimStates } from '../../../../components/InterimStates';
import { PersonTags } from '../../../../components/Protected/PersonTags';
import { PermissionEditor } from '../../../../components/Protected/PermissionEditor';
import { UPDATE_USER_FIELD } from '../../../../mutations/UPDATE_USER_FIELDS';
import { DELETE_USER_RELATIONSHIP } from '../../../../mutations/DELETE_USER_RELATIONSHIP';
import { CREATE_SIBLING_RELATIONSHIP } from '../../../../mutations/CREATE_SIBLING_RELATIONSHIP';
import { CREATE_PARENT_RELATIONSHIP } from '../../../../mutations/CREATE_PARENT_RELATIONSHIP';
import { ADMIN_GET_PERSON } from '../data/ADMIN_GET_PERSON';
import { UserPhoneNumber } from '../../../../components/UserPhoneNumber';
import { CurrentBlockSection, CoursesSection, TeachingSection } from '../shared/ProfileSections';

import styles from './AdminProfileView.module.css';

// --- Sub-components ---

type PersonRef = { id: number; name: string; photoUrl: string };

const RelatedPerson = ({
    person,
    editMode,
    onRemove,
}: {
    person: PersonRef;
    editMode?: boolean;
    onRemove?: () => void;
}) => (
    <div className={styles.relatedPerson}>
        {editMode && onRemove && (
            <Icon name="times circle" color="red" link onClick={onRemove} className={styles.removeIcon} />
        )}
        <Image src={person.photoUrl} circular className={styles.avatarSmall} />
        <Link to={`/people/${person.id}`}>{person.name}</Link>
    </div>
);

const ProfilePhoto = ({
    photoUrl,
    uploadURLSmall,
    canEdit,
    onRefetch,
}: {
    photoUrl: string;
    uploadURLSmall?: string;
    canEdit: boolean;
    onRefetch: () => void;
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [enlarged, setEnlarged] = useState(false);

    const upload = async () => {
        if (!file || !uploadURLSmall) return;
        setUploading(true);
        try {
            await fetch(uploadURLSmall, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': file.size.toString(),
                },
                referrer: 'no-referrer',
            });
            setFile(null);
            onRefetch();
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={styles.photoArea}>
            <Image
                src={photoUrl}
                circular
                className={enlarged ? styles.photoLarge : styles.photoSmall}
                onClick={() => setEnlarged(!enlarged)}
            />
            {canEdit && (
                <div className={styles.photoControls}>
                    {uploading ? (
                        <Label basic loading>Uploading...</Label>
                    ) : (
                        <Label basic>{enlarged ? 'Click to minimize' : 'Click to enlarge'}</Label>
                    )}
                    <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} />
                    <Button compact primary size="tiny" onClick={upload} disabled={!file}>
                        Upload Photo
                    </Button>
                </div>
            )}
        </div>
    );
};

// --- Hooks ---

function useThrottledField(
    field: string,
    updateUser: (opts: any) => Promise<any>,
    id: string | undefined,
    refetch: () => void,
) {
    return useCallback(
        throttle(
            async (value: string) => {
                await updateUser({ variables: { userId: Number(id), value, field } });
                refetch();
            },
            500,
            { leading: false, trailing: true }
        ),
        [updateUser, id, refetch]
    );
}

// --- Main component ---

const AdminProfileView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isAdmin = useIsAdmin();
    const isCenterManager = useIsCenterManager();

    const [editMode, setEditMode] = useState(false);
    const [name, setName] = useState('');
    const [scriptName, setScriptName] = useState('');
    const [address, setAddress] = useState('');
    const [siblingIdentifier, setSiblingIdentifier] = useState('');
    const [parentIdentifier, setParentIdentifier] = useState('');
    const [copied, setCopied] = useState(false);

    const { loading, error, data, refetch } = useQuery(ADMIN_GET_PERSON, {
        variables: { personId: Number(id) },
        skip: !id || isNaN(Number(id)),
    });

    const [updateUser] = useMutation(UPDATE_USER_FIELD);
    const [deleteUserRelationship] = useMutation(DELETE_USER_RELATIONSHIP);
    const [createSiblingRelationship] = useMutation(CREATE_SIBLING_RELATIONSHIP);
    const [createParentRelationship] = useMutation(CREATE_PARENT_RELATIONSHIP);

    useEffect(() => {
        if (data?.user) {
            setName(data.user.name || '');
            setScriptName(data.user.scriptName || '');
            setAddress(data.user.address || '');
        }
    }, [data]);

    const updateName = useThrottledField('name', updateUser, id, refetch);
    const updateScriptName = useThrottledField('scriptName', updateUser, id, refetch);
    const updateAddress = useThrottledField('address', updateUser, id, refetch);

    const archiveUser = useCallback(
        throttle(
            async () => {
                if (!window.confirm('Are you sure you want to archive this person?')) return;
                await updateUser({ variables: { userId: Number(id), value: 'now', field: 'archivedAt' } });
                navigate('/people');
            },
            500,
            { leading: false, trailing: true }
        ),
        [updateUser, id, navigate]
    );

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy!', err);
        }
    };

    const removeRelationship = (userId2: number, type: 'PARENT' | 'SIBLING') => () =>
        deleteUserRelationship({
            variables: { userId1: Number(id), userId2, relationshipType: type },
        }).then(() => refetch());

    if (loading || error || !data?.user) {
        return <InterimStates loading={loading} error={error} label="person's details" />;
    }

    const { user } = data;
    if (user.id == null) return <div>You don't have permission to view this</div>;

    const canEdit = editMode && (isAdmin || isCenterManager);
    const personId = Number(id);

    const hasRelationships =
        user.buddies.length > 0 || user.parents.length > 0 ||
        user.siblings.length > 0 || user.children.length > 0;

    return (
        <ResponsiveContainer>
            <div className={styles.page}>

                {/* Toolbar */}
                <div className={styles.toolbar}>
                    <Icon name="arrow left" size="large" style={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
                    <div>
                        {user.records?.length > 0 && (
                            <Button basic color="green" as={Link} to={`${PATHS.PEOPLE}/${user.id}/records`}>
                                <Icon name="file alternate" /> View records
                            </Button>
                        )}
                        {(isAdmin || isCenterManager) && !user.isArchived && (
                            <Button basic color="blue" onClick={() => {
                                if (!editMode) {
                                    setName(user.name || '');
                                    setAddress(user.address || '');
                                    setScriptName(user.scriptName || '');
                                }
                                setEditMode(!editMode);
                            }}>
                                <Icon name="edit" />
                                {editMode ? 'Exit Edit Mode' : 'Edit Profile'}
                            </Button>
                        )}
                    </div>
                </div>

                {user.isArchived && (
                    <Label color="red" size="large" style={{ marginBottom: '1em', display: 'block' }}>
                        Archived Profile
                    </Label>
                )}

                {/* Profile Header */}
                <div className={styles.card}>
                    <div className={styles.profileHeader}>
                        <ProfilePhoto
                            photoUrl={user.photoUrl}
                            uploadURLSmall={user.uploadURLSmall}
                            canEdit={canEdit}
                            onRefetch={refetch}
                        />
                        <div className={styles.profileInfo}>
                            {canEdit ? (
                                <Input fluid placeholder="Full Name" value={name}
                                    onChange={e => { setName(e.target.value); updateName(e.target.value); }} />
                            ) : (
                                <p className={styles.profileName}>{user.name}</p>
                            )}
                            {(user.scriptName || user.preferredName) && (
                                canEdit ? (
                                    <Input fluid placeholder="Script Name" value={scriptName}
                                        onChange={e => { setScriptName(e.target.value); updateScriptName(e.target.value); }} />
                                ) : (
                                    <p className={styles.profileSubName}>
                                        {user.scriptName} {user.preferredName && `(${user.preferredName})`}
                                    </p>
                                )
                            )}
                            <p className={styles.profileIdentifier}>ID: {user.identifier}</p>
                        </div>
                        <Popup
                            content="Copied profile URL to clipboard!"
                            open={copied}
                            on="click"
                            trigger={
                                <Button
                                    color={copied ? 'green' : 'teal'}
                                    icon={copied ? 'check' : 'copy'}
                                    content={copied ? 'Copied' : 'Share'}
                                    size="small"
                                    onClick={handleCopy}
                                />
                            }
                        />
                    </div>
                </div>

                {/* Relationships editor (edit mode only) */}
                {canEdit && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Relationships</div>
                        <div className={styles.addRelationship}>
                            <span>Add sibling</span>
                            <Input
                                placeholder="Sibling Identifier"
                                value={siblingIdentifier}
                                onChange={e => setSiblingIdentifier(e.target.value)}
                            />
                            <Button onClick={async () => {
                                if (!siblingIdentifier) return;
                                await createSiblingRelationship({
                                    variables: { newSiblingIdentifier: siblingIdentifier, siblingUserId: personId },
                                });
                                setSiblingIdentifier('');
                                refetch();
                            }}>Add</Button>
                        </div>
                        <div className={styles.addRelationship}>
                            <span>Add parent</span>
                            <Input
                                placeholder="Parent Identifier"
                                value={parentIdentifier}
                                onChange={e => setParentIdentifier(e.target.value)}
                            />
                            <Button onClick={async () => {
                                if (!parentIdentifier) return;
                                await createParentRelationship({
                                    variables: { newParentIdentifier: parentIdentifier, childUserId: personId },
                                });
                                setParentIdentifier('');
                                refetch();
                            }}>Add</Button>
                        </div>
                    </div>
                )}

                {/* About */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>About</div>

                    <div className={styles.statBar}>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>
                                {user.dob ? moment().diff(moment(user.dob), 'years') : '—'}
                            </span>
                            <span className={styles.statLabel}>Age</span>
                        </div>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>
                                {Math.round(user.attendancePercentage * 100)}%
                            </span>
                            <span className={styles.statLabel}>Attendance</span>
                        </div>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>{user.truency ?? '—'}</span>
                            <span className={styles.statLabel}>Truancy</span>
                        </div>
                        <div className={styles.statChip}>
                            <span className={styles.statValue}>
                                {user.picturePermission ? '✓' : '✗'}
                            </span>
                            <span className={styles.statLabel}>Picture</span>
                        </div>
                    </div>

                    {hasRelationships && (
                        <div className={styles.infoGrid} style={{ marginBottom: '0.75em' }}>
                            {user.buddies.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Buddy</span>
                                    <div className={styles.relatedPeople}>
                                        {user.buddies.map((b: any) => <RelatedPerson key={b.id} person={b} />)}
                                    </div>
                                </>
                            )}
                            {user.parents.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Parents</span>
                                    <div className={styles.relatedPeople}>
                                        {user.parents.map((b: any) => (
                                            <RelatedPerson key={b.id} person={b} editMode={editMode}
                                                onRemove={removeRelationship(b.id, 'PARENT')} />
                                        ))}
                                    </div>
                                </>
                            )}
                            {user.siblings.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Siblings</span>
                                    <div className={styles.relatedPeople}>
                                        {user.siblings.map((b: any) => (
                                            <RelatedPerson key={b.id} person={b} editMode={editMode}
                                                onRemove={removeRelationship(b.id, 'SIBLING')} />
                                        ))}
                                    </div>
                                </>
                            )}
                            {user.children.length > 0 && (
                                <>
                                    <span className={styles.infoLabel}>Children</span>
                                    <div className={styles.relatedPeople}>
                                        {user.children.map((b: any) => (
                                            <RelatedPerson key={b.id} person={b} editMode={editMode}
                                                onRemove={removeRelationship(b.id, 'PARENT')} />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className={styles.infoGrid}>
                        <span className={styles.infoLabel}>Address</span>
                        <span className={styles.infoValue}>
                            {canEdit ? (
                                <Input fluid value={address}
                                    onChange={e => { setAddress(e.target.value); updateAddress(e.target.value); }} />
                            ) : user.address}
                        </span>

                        <span className={styles.infoLabel}>Phone</span>
                        <span className={styles.infoValue}>
                            <UserPhoneNumber userId={user.id} inEditMode={editMode} />
                        </span>

                        <span className={styles.infoLabel}>Date of Birth</span>
                        <span className={styles.infoValue}>{formatDate(user.dob)}</span>

                        {isAdmin && (
                            <>
                                <span className={styles.infoLabel}>Reg. Document</span>
                                <span className={styles.infoValue}>
                                    {user.haveRegistrationDocument ? 'Yes' : 'No'}
                                    {user.registrationDocumentUrl && (
                                        <> — <a target="_blank" href={user.registrationDocumentUrl} rel="noopener noreferrer">View</a></>
                                    )}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Tags (edit mode only) */}
                {canEdit && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Profile Tags</div>
                        <PersonTags
                            tags={user.tags}
                            refetchPerson={() => refetch({ variables: { personId } })}
                        />
                    </div>
                )}

                {/* Current Block Attendance */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>Current Block Attendance</div>
                    <CurrentBlockSection
                        attendances={user.currentCourseAttendances}
                        personId={personId}
                    />
                </div>

                {/* Past Classes */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>Past Classes</div>
                    <CoursesSection courses={user.courses} personId={personId} />
                </div>

                {/* Classes Teaching */}
                {user.coursesTeaching.length > 0 && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Classes Teaching</div>
                        <TeachingSection courses={user.coursesTeaching} />
                    </div>
                )}

                {/* Permissions (edit mode only) */}
                {canEdit && !isCenterManager && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>Permissions</div>
                        <PermissionEditor
                            userId={personId}
                            refetchPerson={() => refetch({ variables: { personId } })}
                        />
                    </div>
                )}

                {/* Danger Zone (edit mode only) */}
                {canEdit && !isCenterManager && (
                    <div className={styles.card}>
                        <div className={styles.dangerTitle}>Danger Zone</div>
                        <Button color="red" onClick={archiveUser}>Archive Person</Button>
                    </div>
                )}

            </div>
        </ResponsiveContainer>
    );
};

export { AdminProfileView };
